import React from "react";
import { connect } from "react-redux";
import {  Message,Dropdown,Segment,Checkbox } from "semantic-ui-react";
import Validator from "validator";
import api from "../../api";
import Switch from "react-switch";
import utilFunc from "../../util_funs";
import { Form } from "formsy-semantic-ui-react";
import "./integration.css"
import ConfirmDialog from "../Modal/ConfirmDialog";
import validationRule from "../../validation";
import GridList from "../Grid";
import OutboundVoiceWebhook from "./OutboundVoiceWebhook";
import { resetOutbound, hydrateOutbound } from "../../store/outboundVoiceSlice";
import config from "../../config";

// ============================================================================
// HELPER FUNCTIONS - Reusable utilities to reduce code duplication
// ============================================================================

/**
 * Fetch VOIP parsed configuration
 * Centralized function to avoid duplication across handleSwitchVoip, editCredentials, createNew
 */
const fetchVoipParsedValues = async () => {
  try {
    const voipRes = await api.manageUser.getVoipParseConfiguration({});
    const responseData = voipRes?.data;

    if (responseData?.parsed_values !== undefined) {
      return Array.isArray(responseData.parsed_values) ? responseData.parsed_values : [];
    }
    return [];
  } catch (err) {
    console.error("Error fetching VOIP parse configuration:", err);
    return [];
  }
};

/**
 * Parse filter data (handles both JSON strings and arrays)
 * Provides consistent error handling for all filter types
 */
const parseFilters = (filters, filterType = "unknown") => {
  try {
    if (Array.isArray(filters)) {
      return filters;
    }
    if (typeof filters === 'string' && filters.trim() !== '') {
      return JSON.parse(filters);
    }
    return [];
  } catch (e) {
    console.error(`Error parsing ${filterType} filters:`, e);
    return [];
  }
};

/**
 * Build dynamic dropdown options from parsed values and selected fields
 * Used for voice, web, and VOIP filter building
 */
const buildDynamicOptions = (selectedFields, parsedValues) => {
  const dynamicOptions = {};

  selectedFields.forEach((field) => {
    const parsedField = parsedValues?.find((item) => item.key === field);

    if (parsedField && Array.isArray(parsedField.val) && parsedField.val.length > 0) {
      dynamicOptions[field] = parsedField.val.map((option, idx) => ({
        key: idx,
        text: option,
        value: option.trim(),
      }));
    }
  });

  return dynamicOptions;
};

/**
 * Get preferred field from potentially duplicate fields (for VOIP)
 * Prioritizes fields with non-empty value arrays
 */
const getPreferredField = (fieldKey, parsedValues) => {
  const matchingFields = parsedValues?.filter((item) => item.key === fieldKey) || [];

  if (matchingFields.length === 0) return null;
  if (matchingFields.length === 1) return matchingFields[0];

  // Multiple matches - prefer the one with options
  const fieldWithOptions = matchingFields.find(
    (f) => Array.isArray(f.val) && f.val.length > 0
  );
  return fieldWithOptions || matchingFields[0];
};

/**
 * Build dynamic options for VOIP fields (handles duplicate field names)
 */
const buildVoipDynamicOptions = (selectedFields, voipParsedValues) => {
  const dynamicOptions = {};

  selectedFields.forEach((field) => {
    const parsedField = getPreferredField(field, voipParsedValues);

    if (parsedField && Array.isArray(parsedField.val) && parsedField.val.length > 0) {
      dynamicOptions[field] = parsedField.val.map((option, idx) => ({
        key: idx,
        text: option,
        value: option.trim(),
      }));
    }
  });

  return dynamicOptions;
};

/**
 * Process filters for edit mode (parse + extract + build options)
 * Combines multiple operations into a single reusable function
 */
const processFiltersForEdit = (rawFilters, parsedValues, filterType = "voice") => {
  const parsedFilters = parseFilters(rawFilters, filterType);
  const selectedWebhook = parsedFilters.map((filter) => filter.field);
  const selectedValues = parsedFilters.reduce((acc, filter) => {
    acc[filter.field] = filter.selectedValue || null;
    return acc;
  }, {});
  const dynamicOptions = buildDynamicOptions(selectedWebhook, parsedValues);

  return { selectedWebhook, selectedValues, dynamicOptions };
};

/**
 * Process VOIP filters for edit mode (handles duplicates)
 */
const processVoipFiltersForEdit = (rawFilters, voipParsedValues) => {
  const parsedFilters = parseFilters(rawFilters, "voip");
  const selectedWebhook = parsedFilters.map((filter) => filter.field);
  const selectedValues = parsedFilters.reduce((acc, filter) => {
    acc[filter.field] = filter.selectedValue || null;
    return acc;
  }, {});
  const dynamicOptions = buildVoipDynamicOptions(selectedWebhook, voipParsedValues);

  return { selectedWebhook, selectedValues, dynamicOptions };
};

/**
 * Normalize boolean/string values to boolean
 */
const normalizeBoolean = (value) => {
  return value === true || value === "1" || value === 1 || value === "on";
};

/**
 * Deduplicate VOIP fields by key, keeping entries with options
 */
const deduplicateVoipFields = (parsedValues) => {
  if (!parsedValues || !Array.isArray(parsedValues)) return [];

  const uniqueFields = {};

  parsedValues.forEach((field) => {
    const fieldKey = field.key;

    if (!uniqueFields[fieldKey]) {
      uniqueFields[fieldKey] = field;
    } else {
      const existingHasOptions =
        Array.isArray(uniqueFields[fieldKey].val) && uniqueFields[fieldKey].val.length > 0;
      const newHasOptions = Array.isArray(field.val) && field.val.length > 0;

      if (newHasOptions && !existingHasOptions) {
        uniqueFields[fieldKey] = field;
      }
    }
  });

  return Object.values(uniqueFields);
};


class Webhook extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      modal: false,
      loading: false,
      webhook: "",
      webhookStatus: false,
      successMessage: null,
      errors: null,
      contentType: "multipart/form-data",
      workspaceId: null,
      web_workspaceId: null,
      voip_config_id: null,
      workspaceBranches: null,   
      web_workspaceBranches: null,    
      voip_branches: null,
      parsed_values: null,
      web_parsed_values: null,
      voip_parsed_values: null,
      selectedWebhook: [],
      dynamicOptions: {}, // To store the options for each field dynamically
      selectedValues: {}, // To store selected values for dynamic dropdowns,
      web_dynamicOptions: {}, // To store the options for each field dynamically
      web_selectedValues: {}, // To store selected values for dynamic dropdowns
      voip_dynamicOptions: {}, // To store the options for each VOIP field dynamically
      voip_selectedValues: {}, // To store selected values for VOIP dynamic dropdowns
      voip_selectedWebhook: [], // Selected VOIP webhook rules
      required_settings: 0,
      voip_required_setting: 0,
      confirmDialog:false,
      webhook_id:null,
      errorMessage: null,
      dataLoading: null,
      webhook_info:{},
      refreshGrid:false,
      disable: false,
      voice_indices:[],
      web_indices:[],
      fallback_webhook_exists:false
     
     
    };
    this.formRef = React.createRef();
        this.columnLists = [
          { text: "S.No", key: "id", sort: false },
          { text: "Webhook Url", key: "webhook_url", sort: false },
          {text: "Status", key: "status"},
          { text: "Type", key: "content_type" },
          {text: "Voice", key: "voice_on"},
          // { text: "Voice", key: "required_setting" },
          {text: "Chat", key: "web_on"},
          // { text: "Chat", key: "web_required_setting" },
          {text: "VOIP", key: "voip_on"},
          // {text: "VOIP", key: "voip_required_setting"},
          { text: "Is Fallback", key: "fallback_webhook" },
          {
            text: "Action",
            key: "action",
            sort: false,
            event: [
                {
                    key: "editCredentials",
                    params: ["id"],
                    func: (id) =>
                        this.editCredentials(id),
                },
                {
                    key: "deleteCredentials",
                    params: ["id"],
                    func: (id) => this.confirmAction(id),
                },
            ],
        },
        ];
  }
  confirmAction = (id) =>
    this.setState({ webhook_id:id, confirmDialog: true });
  toggleConfirmModal = () =>
    this.setState({ confirmDialog: false, webhook_id: 0 });

  // Copy to clipboard functionality
  copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        this.setState({
          successMessage: 'Webhook URL copied to clipboard!',
        });
        setTimeout(() => {
          this.setState({ successMessage: null });
        }, 2000);
      }).catch((err) => {
        console.error('Failed to copy:', err);
        this.fallbackCopyToClipboard(text);
      });
    } else {
      this.fallbackCopyToClipboard(text);
    }
  };

  // Fallback copy method for older browsers
  fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      this.setState({
        successMessage: 'Webhook URL copied to clipboard!',
      });
      setTimeout(() => {
        this.setState({ successMessage: null });
      }, 2000);
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }
    document.body.removeChild(textArea);
  };

  handleChange = (e) => {
    // ✓ FIXED: Clone state before updating (no direct mutation)
    const webhook_info = {
      ...this.state.webhook_info,
      [e.target.name]: e.target.value
    };

    if (this.state.errors && this.state.errors.webhook) {
      this.setState({ errors: null, webhook_info, disable: false });
    } else {
      this.setState({ webhook_info, disable: false });
    }
  };

  handleSubmit = () => {
    const errors = this.validateForm();
    this.setState({ errors });

    if (Object.keys(errors).length === 0) {
      this.setState({ disable:true});
      let { status, webhook_url, content_type, selectedWebhook,
        selectedValues, web_selectedWebhook,
        web_selectedValues, voip_selectedWebhook,
        voip_selectedValues} = this.state.webhook_info;

      // Construct filters array and convert to JSON string
      const filters = 
        selectedWebhook.map((key) => ({
          field: key,
          selectedValue: selectedValues[key] || null, // Attach selected values for each key
        }));
        // this.setState({loading: true});
        const web_filters = 
        web_selectedWebhook.map((key) => ({
          field: key,
          selectedValue: web_selectedValues[key] || null, // Attach selected values for each key
        }));

        // Construct VOIP filters
        const voip_filters =
        (voip_selectedWebhook || []).map((key) => ({
          field: key,
          selectedValue: voip_selectedValues[key] || null, // Attach selected values for each key
        }));

      // Construct outbound filters for each workspace
      const { outboundVoice } = this.props;
      const outbound_filters = buildOutboundFilters(outboundVoice);

      // Construct payload
      const payload = {
        id:this.state.webhook_id,
        webhookStatus:status,
        webhook: webhook_url,
        content_type,
        filters, 
        web_filters,
        voip_filters,
        outbound_voice_on: outboundVoice.enabled,
        outbound_filters,
        required_setting:this.state.webhook_info.required_setting,
        web_required_setting:this.state.webhook_info.web_required_setting,
        voip_required_setting: this.state.webhook_info.voip_required_setting,
        // ✓ REFACTORED: Use normalizeBoolean helper for consistency
        voice_on: normalizeBoolean(this.state.webhook_info.voice_on),
        voip_on: normalizeBoolean(this.state.webhook_info.voip_on),
        web_on: normalizeBoolean(this.state.webhook_info.web_on),
        voice_indices:this.state.voice_indices,
        web_indices:this.state.web_indices,
        fallback_webhook: normalizeBoolean(this.state.webhook_info.fallback_webhook)
      };


      // if (!status) webhook_url = "";
      api.webhook.createWebhook(payload).then((resp) => {
        if (resp && resp.message) {
          // Reset outbound voice state after successful save
          this.props.resetOutbound();
          // ✓ FIXED: Consistent naming (webhook_info)
          this.setState({
            successMessage: resp.message,
            errorMessage: null,
            modal: false,
            loading: false,
            errors: null,
            webhook_info: {},
            webhook_id: 0,
            refreshGrid: true,
            disable: false
          }, () => {
             utilFunc.scrollTop(800);
            setTimeout(() => {
              this.setState({ successMessage: null,refreshGrid:false,disable:false });
            }, 5000);
          });
        }
      });
    }
  };

  validateForm() {
    const errors = {};
    // if (this.state.webhook_info.status) {
      if (!this.state.webhook_info.webhook_url) errors.webhook = "This field is required.";
      else if (!Validator.isURL(this.state.webhook_info.webhook_url)) {
        errors.webhook = "Invalid webhook url.";
      }
    // }
    setTimeout(() => {
      this.setState({ errors: null });
    }, 5000);
    return errors;
  }
  handleSwitch = (checked) => {
    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      status: checked
    };
    this.setState({ webhook_info });
  };

  handleSwitchvoice = (checked) => {
    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      voice_on: checked
    };

    // If voice is enabled and we don't have the parsed values yet, fetch them
    if (checked && !this.state.parsed_values) {
      api.manageUser.getVoiceWorkspaceBranch({})
        .then((res) => {
          if (res.status) {
            this.setState({
              webhook_info,
              workspaceId: res.data.workspaceId,
              workspaceBranches: res.data.data,
              voice_type: res.data?.type,
              parsed_values: res.data?.parsed_values || [],
              web_workspaceId: res.data?.webparsedetail?.workspaceId,
              web_workspaceBranches: res.data?.webparsedetail?.data,
              web_parsed_values: res.data?.webparsedetail?.parsed_values || [],
              voice_indices: res.data?.key_indices || [],
              web_indices: res.data?.webparsedetail?.key_indices || [],
            });
          }
        })
        .catch((err) => {
          console.error("Error fetching workspace branch data:", err);
          this.setState({ webhook_info });
        });
    } else {
      this.setState({ webhook_info });
    }
                            };
  handleSwitchVoip = async (checked) => {
    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      voip_on: checked
    };

    // If VOIP is enabled, fetch the parsed values (if not already loaded)
    if (checked && (!this.state.voip_parsed_values || this.state.voip_parsed_values.length === 0)) {
      // ✓ REFACTORED: Use centralized helper function
      const voip_parsed_values = await fetchVoipParsedValues();
      this.setState({ webhook_info, voip_parsed_values });
    } else {
      this.setState({ webhook_info });
    }
  };
  handleSwitchweb = (checked) => {
    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      web_on: checked
                           };      

    // If web is enabled and we don't have the parsed values yet, fetch them
    if (checked && !this.state.web_parsed_values) {
      api.manageUser.getVoiceWorkspaceBranch({})
        .then((res) => {
          if (res.status) {
            this.setState({
              webhook_info,
              workspaceId: res.data.workspaceId,
              workspaceBranches: res.data.data,
              voice_type: res.data?.type,
              parsed_values: res.data?.parsed_values || [],
              web_workspaceId: res.data?.webparsedetail?.workspaceId,
              web_workspaceBranches: res.data?.webparsedetail?.data,
              web_parsed_values: res.data?.webparsedetail?.parsed_values || [],
              voice_indices: res.data?.key_indices || [],
              web_indices: res.data?.webparsedetail?.key_indices || [],
            });
          }
        })
        .catch((err) => {
          console.error("Error fetching workspace branch data:", err);
          this.setState({ webhook_info });
        });
    } else {
      this.setState({ webhook_info });
    }
  };                                               
  HandleRequiredSetting=(checked) => {
    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      required_setting: checked ? 1 : 0
    };
    this.setState({ webhook_info, disable: false });
  };
  HandleRequiredSettingforweb=(checked) => {
    let webhook={...this.state.webhook_info} ;
    webhook.web_required_setting=checked ? 1:0;
     this.setState({ webhook_info:webhook,disable:false});
    }
  HandleRequiredSettingforvoip=(checked) => {
    let webhook={...this.state.webhook_info} ; 
    webhook.voip_required_setting=checked ? 1:0;
    this.setState({ webhook_info:webhook,disable:false});
  }
  // ✓ REFACTORED: Removed console.logs, improved readability
  fallbackWebhook=async(checked) => {
    const webhook_info = { ...this.state.webhook_info };

    if(checked){
      this.setState({formLoading:true});
      const fallback_webhook_exists = await this.checkFallbackExistsk(webhook_info.id);

      if(fallback_webhook_exists){
        webhook_info.fallback_webhook = 0;
        this.setState({
          fallback_webhook_exists: true,
          webhook_info,
          disable: false,
          formLoading: false
        });
      }else{
        webhook_info.fallback_webhook = checked ? 1 : 0;
        this.setState({
          fallback_webhook_exists: false,
          webhook_info,
          disable: false,
          formLoading: false
        });
      }
    }else{
      webhook_info.fallback_webhook = 0;
      this.setState({
        fallback_webhook_exists: false,
        webhook_info,
        disable: false,
        formLoading: false
      });
    }

    setTimeout(() => {
      this.setState({formLoading:false,fallback_webhook_exists:false});
    }, 5000);
  };
    
  // ✓ REFACTORED: Removed console.logs, improved error handling
  checkFallbackExistsk=(id=null) => {
    return new Promise((resolve) => {
      api.webhook.checkFallbackExists({ id })
    .then((res) => {
      if(res.status){
        this.setState({loading:false});
       resolve(true);
      }else{
        resolve(false);
      }
    })
    .catch((err) => {
          console.error("Error checking fallback webhook:", err);
          resolve(false);
      });
    });
  };
  // Handle dropdown change
  handleDropdownChange = (e, { value }) => {
    // ✓ FIXED: Clone state before updating
    const webhook_info = {
      ...this.state.webhook_info,
      content_type: value
    };
    this.setState({ webhook_info, disable: false });
  };
  componentDidMount() {
    // Add global copy function for webhook URLs
    window.copyWebhookUrl = (url) => {
      this.copyToClipboard(url);
    };
  }

  componentWillUnmount() {
    // Cleanup global function
    if (window.copyWebhookUrl) {
      delete window.copyWebhookUrl;
    }
  }
  
  openPage() {
    window.open(
      "https://docs.google.com/document/d/1S0lzB7wsoTVpjsEhlnoAEIlPtY_XWnNer1zBmhcnax0/edit?usp=sharing"
    );
  }
  // ✓ REFACTORED: Simplified using consistent pattern
  branchOption = () => {    
    if (!this.state.parsed_values || !Array.isArray(this.state.parsed_values)) {
      return [];
    }
    return this.state.parsed_values.map((key, index) => ({
        key: index,
        text: key.key,
        value: key.key,
    }));
  };

  web_branchOption = () => {    
    if (!this.state.web_parsed_values || !Array.isArray(this.state.web_parsed_values)) {
      return [];
    }
    return this.state.web_parsed_values.map((key, index) => ({
        key: index,
        text: key.key,
        value: key.key,
    }));
  };
  // ✓ REFACTORED: Use buildDynamicOptions helper to reduce duplication
  handleWebhookRule = (e, d) => {
    const selectedKeys = d.value;
    const dynamicOptions = buildDynamicOptions(selectedKeys, this.state.parsed_values);

    const webhook_info = {
      ...this.state.webhook_info,
      selectedWebhook: selectedKeys,
      dynamicOptions
    };

    this.setState({ webhook_info, disable: false });
  };

  handleweb_WebhookRule = (e, d) => {
    const selectedKeys = d.value;
    const dynamicOptions = buildDynamicOptions(selectedKeys, this.state.web_parsed_values);

    const webhook_info = {
      ...this.state.webhook_info,
      web_selectedWebhook: selectedKeys,
      web_dynamicOptions: dynamicOptions
    };

    this.setState({ webhook_info, disable: false });
  };

  // Render additional dropdowns dynamically based on selected items
  renderDynamicDropdowns = () => {
    if (!this.state.webhook_info.selectedWebhook || !Array.isArray(this.state.webhook_info.selectedWebhook)) {
      return null;
    }
    return this.state.webhook_info.selectedWebhook.map((key) => {
      if (this.state.webhook_info.dynamicOptions[key]) {
        return (
          <div className="webhook-filter-rule" key={key}>
            <label className="webhook-filter-label">{`Webhook will trigger if ${key} option equals`}</label>
            <Form.Field>
              <Dropdown
                placeholder={`Choose ${key} value`}
                fluid
                selection
                multiple
                options={this.state.webhook_info.dynamicOptions[key]}
                value={this.state.webhook_info.selectedValues[key] || []}
                onChange={(e, d) => this.handleDynamicSelection(key, d.value)}
              />
            </Form.Field>
          </div>
        );
      }
      return null;
    });
  };
  web_renderDynamicDropdowns = () => {
    if (!this.state.webhook_info.web_selectedWebhook || !Array.isArray(this.state.webhook_info.web_selectedWebhook)) {
      return null;
    }
    return this.state.webhook_info.web_selectedWebhook.map((key) => {
      if (this.state.webhook_info.web_dynamicOptions[key]) {
        return (
          <div className="webhook-filter-rule" key={key}>
            <label className="webhook-filter-label">{`Webhook will trigger if ${key} option equals`}</label>
            <Form.Field>
              <Dropdown
                placeholder={`Choose ${key} value`}
                fluid
                selection
                multiple
                options={this.state.webhook_info.web_dynamicOptions[key]}
                value={this.state.webhook_info.web_selectedValues[key] || []}
                onChange={(e, d) => this.web_handleDynamicSelection(key, d.value)}
              />
            </Form.Field>
          </div>
        );
      }
      return null;
    });
  };
    
  // ✓ FIXED: Simplified and fixed typo (webhhok_info -> webhook_info)
  handleDynamicSelection = (key, value) => {
    const webhook_info = {
      ...this.state.webhook_info,
      selectedValues: {
        ...this.state.webhook_info.selectedValues,
        [key]: value
    }
    };
    this.setState({ webhook_info, disable: false });
  };

  web_handleDynamicSelection = (key, value) => {
    const webhook_info = {
      ...this.state.webhook_info,
      web_selectedValues: {
        ...this.state.webhook_info.web_selectedValues,
        [key]: value
      }
    };
    this.setState({ webhook_info, disable: false });
  };

  // ✓ REFACTORED: Use buildVoipDynamicOptions helper
  handleVoipWebhookRule = (e, d) => {
    const selectedKeys = d.value;
    const dynamicOptions = buildVoipDynamicOptions(selectedKeys, this.state.voip_parsed_values);

    const webhook_info = {
      ...this.state.webhook_info,
      voip_selectedWebhook: selectedKeys,
      voip_dynamicOptions: dynamicOptions
    };

    this.setState({ webhook_info, disable: false });
  };

  // Render VOIP dynamic dropdowns
  voip_renderDynamicDropdowns = () => {
    return this.state.webhook_info.voip_selectedWebhook?.map((key) => {
      if (this.state.webhook_info.voip_dynamicOptions[key]) {
        return (
          <div className="webhook-filter-rule" key={key}>
            <label className="webhook-filter-label">{`Webhook will trigger if ${key} option equals`}</label>
            <Form.Field>
              <Dropdown
                placeholder={`Choose ${key} value`}
                fluid
                selection
                multiple
                options={this.state.webhook_info.voip_dynamicOptions[key]}
                value={this.state.webhook_info.voip_selectedValues[key] || []}
                onChange={(e, d) => this.voip_handleDynamicSelection(key, d.value)}
              />
            </Form.Field>
          </div>
        );
      }
      return null;
    });
  };

  // ✓ FIXED: Simplified and fixed typo
  voip_handleDynamicSelection = (key, value) => {
    const webhook_info = {
      ...this.state.webhook_info,
      voip_selectedValues: {
        ...this.state.webhook_info.voip_selectedValues,
        [key]: value
      }
    };
    this.setState({ webhook_info, disable: false });
  };

  // ✓ REFACTORED: Use deduplicateVoipFields helper, removed excessive console.logs
  voip_branchOption = () => {
    if (!this.state.voip_parsed_values || !Array.isArray(this.state.voip_parsed_values)) {
      return [];
    }

    if (this.state.voip_parsed_values.length === 0) {
      return [];
    }

    const uniqueFields = deduplicateVoipFields(this.state.voip_parsed_values);

    return uniqueFields.map((field, index) => ({
      key: index,
      text: field.key,
      value: field.key,
    }));
  };
  getDataSource = (params) => {
    return api.webhook.loadWebhook(params).then((resp) => {
      if (resp && resp.list) {
        // Transform webhook URLs to include truncation, tooltip, and copy icon
        resp.list = resp.list.map((item) => {
          if (item.webhook_url) {
            item.webhook_url = this.formatWebhookUrl(item.webhook_url);
          }
          return item;
        });
      }
      return resp;
    });
  };

  // Format webhook URL with truncation, tooltip, and copy icon
  formatWebhookUrl = (urlHtml) => {
    // Extract the plain URL from HTML if it exists - using textContent for safety
    const tempDiv = document.createElement('div');
    tempDiv.textContent = urlHtml; // Set as text first
    let url = tempDiv.textContent || urlHtml;

    // If urlHtml appears to be HTML, try to extract text safely using DOMParser
    if (urlHtml.includes('<') || urlHtml.includes('>')) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(urlHtml, 'text/html');
        url = doc.body.textContent || url;
      } catch (e) {
        // Fallback to treating it as plain text
        url = urlHtml.replace(/<[^>]*>/g, '');
      }
    }

    // Truncate URL if longer than 25 characters for compact display
    const maxLength = 25;
    const truncated = url.length > maxLength ? url.substring(0, maxLength) + '...' : url;

    // Escape HTML entities for safe rendering
    const escapeHtml = (str) => {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    };

    const escapedUrl = escapeHtml(url);
    const escapedTruncated = escapeHtml(truncated);
    const escapedUrlForJs = url.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/"/g, '\\"');

    // Return formatted HTML with tooltip and copy icon
    return `
      <div class="webhook-url-cell">
        <span
          class="webhook-url-text"
          title="${escapedUrl}"
        >
          ${escapedTruncated}
        </span>
        <i
          class="copy icon"
          onclick="window.copyWebhookUrl('${escapedUrlForJs}')"
          title="Copy URL"
        ></i>
      </div>
    `;
  }; 
   deleteCredentials = () => {
            this.setState({confirmDialog:false});
            api.webhook
                  .deleteWebhook({ id: this.state.webhook_id })
                  .then((resp) => {
                      
                      if (resp && resp.message) {
                         
                          this.setState({ successMessage: resp.message,  loading: false,
                          webhook_id: 0, refreshGrid:true});
                         
                          utilFunc.scrollTop(800);
                          setTimeout(() => {
                              this.setState({ 
                                successMessage: null,
                                loading: false,
                               webhook_id: 0,refreshGrid:false });
                          }, 4000);
                      } else if (resp.errorMessage) {
                        this.setState({  errorMessage: resp.errorMessage,  loading: false,
                          webhook_id: 0, });
                         
                      }
                  })
                  .catch((err) => {
                      console.log(err);
                      this.setState({ 
                         loading: false,
                        webhook_id: 0, });
                  });
          
      };
  
  // ✓ REFACTORED: Simplified editCredentials using helper functions
  editCredentials = async (id) => {
           this.setState({fallback_webhook_exists:false});

    if (!id) return;

    let resp = null;

    try {
      resp = await api.webhook.getWebhookInfo({ id });
      if (!resp || !resp.webhhok_info) return;

      const res = await api.manageUser.getVoiceWorkspaceBranch({});
      if (!res.status) return;
                  
      // ✓ Use centralized helper instead of duplicating logic
      const voip_parsed_values = await fetchVoipParsedValues();

      const parsed_values = res.data?.parsed_values || [];
      const web_parsed_values = res.data?.webparsedetail?.parsed_values || [];

      // ✓ REFACTORED: Use helper functions to process filters
      const voiceFilters = processFiltersForEdit(resp.webhhok_info.filters, parsed_values, "voice");
      const webFilters = processFiltersForEdit(resp.webhhok_info.web_filters, web_parsed_values, "web");
      const voipFilters = processVoipFiltersForEdit(resp.webhhok_info.voip_filters, voip_parsed_values);

      // ✓ FIXED: Spread webhhok_info FIRST, then override specific fields
      const webhook_info = {
        ...resp.webhhok_info,
        selectedWebhook: voiceFilters.selectedWebhook,
        dynamicOptions: voiceFilters.dynamicOptions,
        selectedValues: voiceFilters.selectedValues,
        web_selectedWebhook: webFilters.selectedWebhook,
        web_dynamicOptions: webFilters.dynamicOptions,
        web_selectedValues: webFilters.selectedValues,
        voip_selectedWebhook: voipFilters.selectedWebhook,
        voip_dynamicOptions: voipFilters.dynamicOptions,
        voip_selectedValues: voipFilters.selectedValues
      };
                  
                  // Hydrate outbound voice state from saved data
      const outboundVoiceOn = normalizeBoolean(resp.webhhok_info.outbound_voice_on);
      let savedOutboundFilters = parseFilters(resp.webhhok_info.outbound_filters, "outbound");
                  
                  if (outboundVoiceOn || savedOutboundFilters.length > 0) {
                    const selectedWorkspaces = savedOutboundFilters.map((ws) => ws.workspace_id);
                    const parsedFields = {};
                    const selectedValuesOutbound = {};
                    const requiredSettings = {};

                    savedOutboundFilters.forEach((ws) => {
                      parsedFields[ws.workspace_id] = (ws.fields || []).map((f) => f.field);
                      
                      selectedValuesOutbound[ws.workspace_id] = {};
                      (ws.fields || []).forEach((f) => {
                        if (f.selectedValue && f.selectedValue.length > 0) {
                          selectedValuesOutbound[ws.workspace_id][f.field] = f.selectedValue;
                        }
                      });

          requiredSettings[ws.workspace_id] = normalizeBoolean(ws.required_setting);
                    });

                    this.props.hydrateOutbound({
                      enabled: outboundVoiceOn,
                      selectedWorkspaces,
                      parsedFields,
                      selectedValues: selectedValuesOutbound,
                      requiredSettings,
                    });
                  } else {
                    this.props.resetOutbound();
                  }

      // ✓ FIXED: Set all state in a single setState call
      this.setState({
        workspaceId: res.data.workspaceId,
        workspaceBranches: res.data.data,
        voice_type: res.data?.type,
        parsed_values,
        web_workspaceId: res.data?.webparsedetail?.workspaceId,
        web_workspaceBranches: res.data?.webparsedetail?.data,
        web_parsed_values,
        voip_parsed_values,
        voice_indices: res.data?.key_indices || [],
        web_indices: res.data?.webparsedetail?.key_indices || [],
        webhook_info,
        webhook_id: id,
        modal: true
      });

                     this.createNew(false);
    } catch (err) {
      console.error("Error in editCredentials:", err);
      // ✓ FIXED: Only use resp if it was successfully fetched
      if (resp && resp.webhhok_info) {
        this.setState({
          webhook_info: resp.webhhok_info,
          webhook_id: id,
          modal: true
            });
        this.createNew(false);
                  }
          }
      };

  // ✓ REFACTORED: Simplified createNew using helper functions
  createNew = async (old) => {
        this.setState({fallback_webhook_exists:false});

        if(!old){  
      this.setState({ modal: true });
      return;
    }

    try {
      const res = await api.manageUser.getVoiceWorkspaceBranch({});
      if (!res.status) return;

      // ✓ Use centralized helper instead of duplicating logic
      const voip_parsed_values = await fetchVoipParsedValues();

              this.setState({
                workspaceId: res.data.workspaceId,
                  workspaceBranches: res.data.data,
                  voice_type: res.data?.type,
        parsed_values: res.data?.parsed_values || [],
                  web_workspaceId: res.data?.webparsedetail?.workspaceId,
                  web_workspaceBranches: res.data?.webparsedetail?.data,
        web_parsed_values: res.data?.webparsedetail?.parsed_values || [],
        voip_parsed_values,
                  voice_indices: res.data?.key_indices || [],
                  web_indices: res.data?.webparsedetail?.key_indices || [],
              }, () => {
        // ✓ FIXED: Use consistent naming (webhook_info) and simplified object creation
        const webhook_info = {
          status: true,
          webhook_url: "",
          required_settings: 0,
          contentType: "multipart/form-data",
          selectedWebhook: [],
          dynamicOptions: {},
          selectedValues: {},
          web_selectedWebhook: [],
          web_dynamicOptions: {},
          web_selectedValues: {},
          voip_selectedWebhook: [],
          voip_dynamicOptions: {},
          voip_selectedValues: {},
          fallback_webhook: 0
        };

        this.setState({ webhook_info, webhook_id: 0, modal: true });
              });
    } catch (err) {
      console.error("Error in createNew:", err);
        }
      };
  
  // ✓ FIXED: Consistent naming (webhook_info)
    clearState = (data = null) => {
    return new Promise((resolve) => {
        let elem = {
          modal: false,
          webhook_id:0,
        webhook_info: {},
          errorMessage:null,
          successMessage:null,
          errors:null,
        };
  
        if (data) {
          elem = { ...elem, ...data };
        }

        // Reset outbound voice state
        this.props.resetOutbound();
        this.setState(elem, () => {
          resolve();
        });
      });
    };
    toggleModal = () => this.clearState();

  submitForm = () => this.formRef.current.submit();


    createCredentials = () => {
      const errorLabel = <div className="red" pointing={"true"} />;
    const options = [
      { key: 'json', text: 'JSON', value: 'application/json' },
      { key: 'form', text: 'Form Data', value: 'multipart/form-data' },
    ];
      return (
        <div
          className={this.state.modal ? "modal fade show " : "modal fade"}
          data-backdrop="static"
          tabIndex="-1"
          role="dialog"
          aria-labelledby="staticBackdrop"
          aria-hidden="true"
          style={{ display: this.state.modal ? "block" : "none" }}
        >
          {this.state.formLoading && <div className="ui loading form"></div>}
          {this.state.successMessage && <div className="ui success message">{this.state.successMessage}</div>}
          
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <button
                type="button"
                className="close linear-gradient"
                data-dismiss="modal"
                aria-label="Close"
                onClick={this.toggleModal} 
              >
                X
              </button>
              <div className="modal-header">
                <h5 className="modal-title pl-0">{"Webhook"}</h5>
              </div>
              <div className="modal-body webhookform">
              <div className="webhook-section-card">
                <div className="webhook-section-header">
                  <div className="webhook-section-title">
                    <label className="webhook-main-label">Enable Webhook</label>
                    <Switch
                      onChange={this.handleSwitch}
                      checked={this.state.webhook_info.status==true || this.state.webhook_info.status=='active' ? true: false}
                      onColor="#86d3ff"
                      onHandleColor="#2693e6"
                      handleDiameter={30}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                      height={20}
                      width={48}
                      className="react-switch"
                      id="material-switch"
                    />
                  </div> 
                  </div>
                <div className="webhook-card-content">
                  <p className="webhook-section-description">Enable or disable all webhook events.</p>
                  {(this.state.errors && (this.state.webhook_info.status==false || this.state.webhook_info.status=='inactive')) && (
                    <div className="webhook-error-message" style={{marginTop: '10px'}}>
                      <p className="text-danger" style={{margin: 0}}>Enable and enter a webhook URL to save.</p>
                        </div>
                      )}
                </div>
              </div>
                 <Form  
                  noValidate
                  autoComplete="off"
                  onValidSubmit={this.handleSubmit}
                  className="kt-form kt-form--label-right"
                  ref={this.formRef}
                  style={{ fontFamily:'Poppins", sans-serif' }}
                  id="webhookinfo-form"
                >
                 {(this.state.webhook_info.status==true || this.state.webhook_info.status=='active') && <React.Fragment>                 
                   <div className="webhook-section-card">
                    <div className="webhook-card-content" style={{padding: '20px 15px'}}>
                      <div className="webhook-input-group">
                        <label className="webhook-input-label">
                          Webhook Url <span style={{color: 'red'}}>*</span>
                      </label>
                      <Form.Input
                        type="text"
                        autoComplete="off"
                        name="webhook_url"
                          placeholder="Enter webhook URL"
                          className="webhook-url-input"
                        value={this.state.webhook_info.webhook_url}
                        onChange={this.handleChange}
                        error={
                          this.state.errors && this.state.errors.webhook
                            ? true
                            : false
                        }
                      />
                      {this.state.errors && (
                          <div className="webhook-error-message">
                          {this.state.errors.webhook}
                        </div>
                      )}
                    </div>
                      <div className="webhook-input-group">
                    <Form.Field>
                          <label className="webhook-input-label">Content Type</label>
                      <Dropdown
                        placeholder="Select Content Type"
                        fluid
                        selection
                        options={options}
                        onChange={this.handleDropdownChange}
                        value={this.state.webhook_info.content_type}
                      />
                    </Form.Field>
                     </div>
                    </div>
                   </div>
                      <div className="webhook-section-card">
                        <div className="webhook-section-header">
                          <div className="webhook-section-title">
                            <label className="webhook-main-label">Inbound Voice</label>
                    <Switch
                      onChange={this.handleSwitchvoice}
                            checked={
                              this.state.webhook_info.voice_on === true || 
                              this.state.webhook_info.voice_on === "1" ||
                              this.state.webhook_info.voice_on === "on"
                            }
                      onColor="#86d3ff"
                      onHandleColor="#2693e6"
                      handleDiameter={30}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                      height={20}
                      width={48}
                      className="react-switch"
                      id="material-switch"
                    />
                  </div> 
                  </div>

                        {(this.state.webhook_info.fallback_webhook==0 && (this.state.webhook_info.voice_on=='1' || this.state.webhook_info.voice_on==true)) && (
                          <div className="webhook-card-content">
                            {this.state.webhook_info.voice_on && this.state.parsed_values && (
                              <div className="webhook-filter-rule">
                                <label className="webhook-filter-label">Add webhook filtering rule</label>
                        <Form.Field>                          
                          <Dropdown
                                    placeholder="Searchable Select Rule"
                            fluid
                            multiple
                            search
                            selection
                                    icon="search"
                            options={this.branchOption()}
                            onChange={this.handleWebhookRule}
                                    value={this.state.webhook_info.selectedWebhook || []}
                                    className="webhook-searchable-dropdown"
                          />
                        </Form.Field>
                      </div>
                            )}
                  {this.renderDynamicDropdowns()}
                            <div className="webhook-checkbox-container">
                  <Checkbox
                        onChange={(e, data)=>this.HandleRequiredSetting(data.checked)}
                        checked={this.state.webhook_info.required_setting==1 || this.state.webhook_info.required_setting==true ? true: false}
                      />
                              <label className="webhook-checkbox-label" onClick={() => this.HandleRequiredSetting(!(this.state.webhook_info.required_setting==1 || this.state.webhook_info.required_setting==true))}>
                                Use the "if all required fields are gathered" rule within the workspace to determine when to pass a payload
                              </label>
                 </div>
                          </div>
                        )}
                      </div>
                      <OutboundVoiceWebhook
                        showOutbound={this.state.webhook_info.fallback_webhook}
                      />                 

                 <div className="webhook-section-card">
                  <div className="webhook-section-header">
                    <div className="webhook-section-title">
                      <label className="webhook-main-label">Chat</label>
                    <Switch
                      onChange={this.handleSwitchweb}
                        checked={
                          this.state.webhook_info.web_on === true ||
                          this.state.webhook_info.web_on === "1" ||
                          this.state.webhook_info.web_on === "on"
                        }
                      onColor="#86d3ff"
                      onHandleColor="#2693e6"
                      handleDiameter={30}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                      height={20}
                      width={48}
                      className="react-switch"
                      id="material-switch"
                    />
                  </div>    
                  </div>

                  {(this.state.webhook_info.fallback_webhook==0 && (this.state.webhook_info.web_on=='1' || this.state.webhook_info.web_on==true)) && (
                    <div className="webhook-card-content">
                      {this.state.web_parsed_values && (
                        <div className="webhook-filter-rule">
                          <label className="webhook-filter-label">Add webhook filtering rule</label>
                        <Form.Field>                          
                          <Dropdown
                              placeholder="Searchable Select Rule"
                            fluid
                            multiple
                            search
                            selection
                              icon="search"
                            options={this.web_branchOption()}
                            onChange={this.handleweb_WebhookRule}
                              value={this.state.webhook_info.web_selectedWebhook || []}
                              className="webhook-searchable-dropdown"
                          />
                        </Form.Field>
                      </div>
                      )}
                  {this.web_renderDynamicDropdowns()}
                      <div className="webhook-checkbox-container">
                  <Checkbox
                        onChange={(e, data)=>this.HandleRequiredSettingforweb(data.checked)}
                        checked={this.state.webhook_info.web_required_setting==1 || this.state.webhook_info.web_required_setting==true ? true: false}
                      />
                        <label className="webhook-checkbox-label" onClick={() => this.HandleRequiredSettingforweb(!(this.state.webhook_info.web_required_setting==1 || this.state.webhook_info.web_required_setting==true))}>
                          Use the "if all required fields are gathered" rule within the workspace to determine when to pass a payload
                        </label>
                      </div>
                    </div>
                  )}
                 </div>

{/* webhook for voip */}
                  <div className="webhook-section-card">
                    <div className="webhook-section-header">
                      <div className="webhook-section-title">
                        <label className="webhook-main-label">VOIP</label>
                        <Switch
                          onChange={this.handleSwitchVoip}
                          checked={
                            this.state.webhook_info.voip_on === true ||
                            this.state.webhook_info.voip_on === "1" ||
                            this.state.webhook_info.voip_on === "on"
                          }
                          onColor="#86d3ff"
                          onHandleColor="#2693e6"
                          handleDiameter={30}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                          height={20}
                          width={48}
                          className="react-switch"
                          id="material-switch"
                        />
                      </div>
                  </div>
                
                    {(this.state.webhook_info.fallback_webhook==0 && (this.state.webhook_info.voip_on=='1' || this.state.webhook_info.voip_on==true)) && (
                      <div className="webhook-card-content">
                        {this.state.webhook_info.voip_on && this.state.voip_parsed_values && (
                          <div className="webhook-filter-rule">
                            <label className="webhook-filter-label">Add webhook filtering rule</label>
                            <Form.Field>
                              <Dropdown
                                placeholder={this.state.voip_parsed_values.length === 0 ? "No VOIP parse fields configured" : "Searchable Select Rule"}
                                fluid
                                multiple
                                search
                                selection
                                icon="search"
                                options={this.voip_branchOption()}
                                onChange={this.handleVoipWebhookRule}
                                value={this.state.webhook_info.voip_selectedWebhook || []}
                                disabled={this.state.voip_parsed_values.length === 0}
                                className="webhook-searchable-dropdown"
                              />
                            </Form.Field>
                            {this.state.voip_parsed_values.length === 0 && (
                              <div style={{color: '#f2711c', marginTop: '5px', fontSize: '12px'}}>
                                Please configure VOIP parse settings first to enable webhook filtering rules.
                              </div>
                            )}
                          </div>
                        )}
                        {this.voip_renderDynamicDropdowns()}
                        <div className="webhook-checkbox-container">
                  <Checkbox
                            onChange={(e, data)=>this.HandleRequiredSettingforvoip(data.checked)}
                            checked={this.state.webhook_info.voip_required_setting==1 || this.state.webhook_info.voip_required_setting==true ? true: false}
                          />
                          <label className="webhook-checkbox-label" onClick={(e) => {
                            if (e.target.tagName !== 'A') {
                              this.HandleRequiredSettingforvoip(!(this.state.webhook_info.voip_required_setting==1 || this.state.webhook_info.voip_required_setting==true));
                            }
                          }}>
                            Use the "if all required fields are gathered" rule within the{' '}
                            <a
                              href={`${config.baseUrl}/voip`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{color: '#2693e6', textDecoration: 'underline'}}
                              onClick={(e) => e.stopPropagation()}
                            >
                              VOIP settings
                            </a>
                            {' '}to determine when to pass a payload
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

               <div className="webhook-section-card">
                <div className="webhook-card-content">
                  <div className="webhook-checkbox-container">
                    <Checkbox
                        onChange={(e, data)=>this.fallbackWebhook(data.checked)}
                        checked={this.state.webhook_info.fallback_webhook==1 || this.state.webhook_info.fallback_webhook==true ? true: false}
                      />
                    <label className="webhook-checkbox-label" onClick={() => this.fallbackWebhook(!(this.state.webhook_info.fallback_webhook==1 || this.state.webhook_info.fallback_webhook==true))}>
                      Fallback Webhook - Trigger only when no other account webhooks fire
                    </label>
                  </div>
                  {this.state.fallback_webhook_exists && (
                    <div style={{marginTop: '10px'}}>
                      <p className="text-danger" style={{margin: 0}}>Fallback webhook already exists.</p>
                    </div>
                  )}
                 </div>
               </div>
               
                 </React.Fragment> }
                <div className="form-group row" style={{marginTop:'10px'}}>
                  <div class="col-lg-12 col-md-12 col-sm-12 form-group-sub ">
                    <button
                      type="submit"
                      disabled={this.state.disable}
                      style={{ float: "right" }}
                      className="btn linear-gradient yr-submitbtn btn-sm"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </Form>
              </div>
             
            </div>
          </div>
        </div>
      );
      

      
    };

  render() {
    
    return (
      <React.Fragment>
        <div className="row" id="webhookportal">
          <div className="col-lg-12 col-md-12 col-sm-12">
            {this.state.modal && this.createCredentials()}
            {this.state.confirmDialog && (
              <ConfirmDialog
                show={this.state.confirmDialog}
                toggleModal={this.deleteCredentials}
                closeAction={this.toggleConfirmModal}
                title="Confirm your action"
                message={"Do you really want to delete this credential ?"}
              />
            )}         
                       {this.state.loading && <div className="ui loading form"></div>}
            <div className="kt-portlet__body kt-portlet__body--fit ">
            {this.state.successMessage && (
                         <Message color={"green"}>{this.state.successMessage}</Message>
                       )}
                       {this.state.errorMessage && (
                         <Message color={"red"}>{this.state.errorMessage}</Message>
                       )}
              <div class="form-group row">
                <div class="col-lg-12 col-md-12 col-sm-12 form-group-sub ">
                  <button
                    type="submit"
                    style={{ float: "center" }}
                    className="btn linear-gradient yr-submitbtn btn-sm"
                    onClick={this.openPage}
                  >
                    Webhook Tutorial Docs
                  </button>
                
                </div>
              </div>
              <div className="yr-manageUser-table">
                        <Segment>
                            <div className="webhook-integration act--integration">
                                <GridList
                                    dataSource={(params) => this.getDataSource(params)}
                                    dataColumns={this.columnLists}
                                    refreshGrid={this.state.refreshGrid}
                                >
                                    <div className="col-md-8 kt-margin-b-20-tablet-and-mobile">
                                        <button
                                            style={{ marging: "0 0 15px 0", float: "right" }}
                                            type="button"
                                            className="btn linear-gradient yr-submitbtn"
                                            onClick={()=>this.createNew(true)}
                                        >
                                            Create New
                                        </button>
                                    </div>
                                </GridList>
                            </div>
                        </Segment>
                    </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}
  /**
   * Build outbound webhook filters payload
   *
   * INPUT:
   * - outboundVoice (Redux slice)
   *
   * OUTPUT:
   * - Array of workspace-level outbound webhook rules
   *
   * Structure:
   * [
   *   {
   *     workspace_id,
   *     workspace_name,
   *     required_setting,
   *     fields: [
   *       { field, selectedValue }
   *     ]
   *   }
   * ]
   *
   * Used during webhook SAVE to send normalized outbound data to backend
   */
 const buildOutboundFilters = (outboundVoice) => {
    if (!outboundVoice?.enabled) return [];

    // Create a quick lookup for workspace names
    const workspaceMap = outboundVoice.workspaces.reduce((acc, w) => {
      acc[w.workspace_id] = w.workspace_name;
      return acc;
    }, {});

    return outboundVoice.selectedWorkspaces.map((wsId) => ({
      workspace_id: wsId,
      workspace_name: workspaceMap[wsId] || "",

      // Convert boolean → integer for backend consistency
      required_setting: outboundVoice.requiredSettings[wsId] ? 1 : 0,

      // Workspace-specific filter rules
      fields: (outboundVoice.parsedFields[wsId] || []).map((field) => ({
        field,
        selectedValue:
          outboundVoice.selectedValues[wsId]?.[field] || [],
      })),
    }));
  }; 

/**
 * outboundVoice (from Redux)
 *
 * Holds Outbound Voice Webhook configuration:
 * - enabled: whether outbound voice is ON
 * - workspaces: available outbound workspaces
 * - selectedWorkspaces: chosen workspaces for outbound
 * - parsedFields: selected filter fields per workspace
 * - selectedValues: selected values per field per workspace
 * - requiredSettings: "all required fields gathered" rule per workspace
 */
const mapStateToProps = (state) => ({
  outboundVoice: state.outboundVoice,
});

/**
 * Map Redux actions to component props
 *
 * These actions can be called directly from props:
 * - resetOutbound():
 *   Clears outbound voice configuration (used on modal close / cancel)
 *
 * - hydrateOutbound(payload):
 *   Prefills outbound state when editing an existing webhook
 */
const mapDispatchToProps = {
  resetOutbound,
  hydrateOutbound,
};

/**
 * Connect Redux state and actions to Webhook component
 *
 * This allows Webhook to:
 * - Read outboundVoice state
 * - Reset or hydrate outbound configuration when needed
 */
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Webhook);
